import { useCallback, useEffect, useState } from "react";
import { Download, DatabaseBackup, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RestoreDialog } from "@/components/privacy/RestoreDialog";

const KIND = { monthly: "Mensile", daily: "Giornaliero", safety: "Sicurezza (pre-ripristino)" };

const mb = (n) => `${(n / 1048576).toFixed(2)} MB`;

export const BackupsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [restores, setRestores] = useState([]);
  const load = useCallback(() => {
    api.get("/privacy/backups").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/privacy/restores").then((r) => setRestores(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/privacy/backups/run"); toast.success(`Backup completato: ${r.data.documents.toLocaleString("it-IT")} record in ${r.data.collections} archivi`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ok = rows.filter((r) => r.status === "ok");
  return (
    <div data-testid="backups-tab">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4 border-l-4 border-foreground bg-white px-5 py-4">
        <div className="max-w-2xl text-sm">
          <div className="font-semibold">Backup automatico ogni notte alle 2:30</div>
          <p className="mt-1 text-muted-foreground">Copia completa del database nell'archivio cloud. Si conservano gli ultimi 30 giorni e il primo backup di ogni mese per 12 mesi; i più vecchi vengono sovrascritti. Gli allegati sono già salvati nell'archivio cloud.</p>
          <p className="mt-1 text-xs text-muted-foreground">{ok.filter((r) => r.kind === "daily").length} giornalieri · {ok.filter((r) => r.kind === "monthly").length} mensili disponibili</p>
        </div>
        <Button onClick={run} disabled={busy} data-testid="backup-run-button">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <DatabaseBackup className="mr-1 h-4 w-4" />}Esegui backup ora</Button>
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="backups-empty">Nessun backup ancora eseguito.</p> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Esito</TableHead><TableHead className="text-right">Record</TableHead><TableHead className="text-right">Dimensione</TableHead><TableHead>Avviato da</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                  <TableCell className="font-mono text-xs">{fmtDateTime(b.at)}</TableCell>
                  <TableCell className="text-sm">{KIND[b.kind] || b.kind}</TableCell>
                  <TableCell>{b.status === "ok" ? <span className="bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-emerald-800">Completato</span> : <span className="bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-red-800" title={b.error}>Non riuscito</span>}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{b.documents?.toLocaleString("it-IT") ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{b.size ? mb(b.size) : "—"}</TableCell>
                  <TableCell className="text-sm">{b.by}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">{b.status === "ok" && <><Button size="sm" variant="ghost" onClick={() => setRestoring(b)} data-testid={`backup-restore-${b.id}`}><RotateCcw className="mr-1 h-4 w-4" />Ripristina…</Button><Button size="sm" variant="ghost" onClick={() => downloadFile(`/privacy/backups/${b.id}/download`, "backup.zip")} data-testid={`backup-download-${b.id}`}><Download className="mr-1 h-4 w-4" />Scarica</Button></>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {restores.length > 0 && <div className="mt-8"><div className="micro-label mb-2 border-b pb-1">Ripristini eseguiti</div>
        {restores.map((r) => <div key={r.id} className="flex flex-wrap justify-between gap-2 border-b py-2 text-sm" data-testid={`restore-run-${r.id}`}><span>{fmtDateTime(r.at)} · {r.by} · backup del {fmtDateTime(r.backup_at)}</span><span className="text-xs text-muted-foreground">{Object.keys(r.restored).length} archivi: {Object.keys(r.restored).join(", ")}</span></div>)}
      </div>}
      <RestoreDialog backup={restoring} onClose={() => setRestoring(null)} onDone={() => { setRestoring(null); load(); }} />
    </div>
  );
};
